import React, { useState, useEffect } from "react";
import { Table, Space, Card, Button, Select, DatePicker, Row, Col, message, Tag } from "antd";
import DashboardLayout from "../LayoutNavbar/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../LayoutNavbar/Navbars/DashboardNavbar";
import { FileOpenOutlined, FileDownloadOutlined } from "@mui/icons-material";
import moment from "moment";
import Footer from "Common/Footer";
import ManagerReportService from "Services/EmployeeServices/ManagerReportService";
import { ReportPeriodTypeOptions } from "Constants/Enums/ReportPeriodTypeEnum";
import jsPDF from "jspdf";
import "jspdf-autotable";

const { Option } = Select;

function EmployeeExpenseRequest() {
  const [departmentName, setDepartmentName] = useState("");
  const [departmentId, setDepartmentId] = useState(null);
  // Point 13: day/week/month period selector. periodType "" = All Time.
  const [periodType, setPeriodType] = useState("");
  const [referenceDate, setReferenceDate] = useState(null);
  const [periodLabel, setPeriodLabel] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const BASE_URL = process.env.REACT_APP_Base_URL;

  // --- Fetch Manager's Department ---
  useEffect(() => {
    const fetchDepartment = async () => {
      try {
        const res = await ManagerReportService.getMyDepartment(); //  updated
        const dept = res?.data || res;
        if (dept && dept.id) {
          setDepartmentId(dept.id);
          setDepartmentName(dept.name || "My Department");
        } else {
          message.warning("Department not found for this manager.");
        }
      } catch (error) {
        console.error("Error fetching department:", error);
        message.error("Failed to load department info.");
      }
    };
    fetchDepartment();
  }, []);

  // --- Fetch Expense Data when filters change ---
  useEffect(() => {
    if (departmentId) {
      fetchData(departmentId, periodType, referenceDate);
    }
  }, [departmentId, periodType, referenceDate]);

  const fetchData = async (deptId, pType, refDate) => {
    try {
      setLoading(true);
      const res = await ManagerReportService.getExpenseReport(deptId, pType, refDate);
      // Point 13: response is now { period, data }. Read the list from data.data.
      const body = res.data || {};
      const reportData = body.data;
      setPeriodLabel(body.period?.periodLabel || "");
      if (Array.isArray(reportData)) {
        setData(
          reportData.map((item) => ({
            key: item.id,
            expenseManagementId: item.id,
            employeeName: item.employee
              ? `${item.employee.firstName} ${item.employee.lastName}`
              : "-",
            email: item.employee?.email || "-",
            date: item.date,
            description: item.description,
            charges: item.charges,
            receipt: item.receipt,
            calculationSheet: item.calculationSheet,
            status: item.status,
            approver: item.approver ? `${item.approver.fullName}` : "-",
            approvedDate: item.approvedDate,
          }))
        );
      } else {
        setData([]);
        message.warning("No expense records found.");
      }
    } catch (error) {
      console.error("Error fetching expense report:", error);
      message.error("Failed to load expense report.");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePdfPreview = (filePath) => {
    if (!filePath) return;
    const isFullUrl = /^https?:\/\//i.test(filePath);
    const fullUrl = isFullUrl
      ? filePath
      : `${BASE_URL}${filePath.startsWith("/") ? "" : "/"}${filePath}`;
    window.open(fullUrl, "_blank", "noopener,noreferrer");
  };

  const handleDownloadPDF = () => {
    try {
      if (!data || data.length === 0) {
        message.warning("No data available to generate PDF");
        return;
      }

      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.text("EMPLOYEE EXPENSE REPORT", 105, 20, { align: "center" });

      doc.setFontSize(10);
      doc.text(`Department: ${departmentName}`, 14, 35);

      if (periodLabel) {
        doc.text(`Period: ${periodLabel}`, 14, 41);
      }

      doc.text(
        `Generated: ${new Date().toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })}`,
        14,
        47
      );

      const headers = [
        ["Employee", "Email", "Date", "Description", "Charges", "Status", "Approver", "Approved"],
      ];
     const tableData = data.map((item) => [
  item.employeeName || "-",
  item.email || "-",
  moment(item.date).format("MMM DD, YYYY"),
  item.description?.substring(0, 25) + (item.description?.length > 25 ? "..." : "") || "-",
  `$${parseFloat(item.charges || 0).toFixed(2)}`,
  getStatusText(item.status),
  item.approver || "-",
  item.approvedDate ? moment(item.approvedDate).format("MMM DD, YYYY") : "N/A",
]);

      doc.autoTable({
        startY: 60,
        head: headers,
        body: tableData,
        theme: "grid",
        styles: { fontSize: 8, cellPadding: 2 },
      });

      doc.save(`Expense-Report-${departmentName}.pdf`);
      message.success("PDF report downloaded successfully!");
    } catch (error) {
      console.error("Error generating PDF:", error);
      message.error("Failed to generate PDF report");
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 0:
        return "Pending";
      case 1:
        return "In Process";
      case 2:
        return "Approved";
      case 3:
        return "Rejected";
      default:
        return "Unknown";
    }
  };

  const columns = [
  { 
    title: "Employee", 
    key: "employee",
    render: (_, record) => (
      <div style={{ lineHeight: '1.4' }}>
        <div style={{ fontWeight: 600, color: '#262626' }}>{record.employeeName}</div>
        <div style={{ fontSize: '12px', color: '#8c8c8c' }}>{record.email}</div>
      </div>
    )
  },
  {
    title: "Date",
    dataIndex: "date",
    key: "date",
    render: (date) => moment(date).format("MMM DD, YYYY"),
  },
  { 
    title: "Charges", 
    dataIndex: "charges", 
    key: "charges",
    render: (amount) => (
      <span style={{ fontWeight: 700, color: '#141414' }}>
        ${parseFloat(amount || 0).toFixed(2)}
      </span>
    )
  },
  {
    title: "Attachments",
    key: "attachments",
    render: (_, record) => (
      <Space size="middle">
        {record.receipt && (
          <FileOpenOutlined 
            className="action-icon-blue"
            onClick={() => handlePdfPreview(record.receipt)} 
          />
        )}
        {record.calculationSheet && (
          <FileOpenOutlined 
            className="action-icon-green"
            onClick={() => handlePdfPreview(record.calculationSheet)} 
          />
        )}
      </Space>
    ),
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (status) => (
      <Tag 
        bordered={false} 
        color={status === 2 ? "success" : status === 3 ? "error" : "processing"}
      >
        {getStatusText(status).toUpperCase()}
      </Tag>
    ),
  },
];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card
        className="card-professional"
        title={`Employee Expense Report — ${departmentName || "My Department"}`}
        extra={<Button onClick={() => window.history.back()}>Back</Button>}
      >
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Space size="middle">
              <Select value={periodType} onChange={setPeriodType} style={{ width: 140 }}>
                <Option value="">All Time</Option>
                {ReportPeriodTypeOptions.map((o) => (
                  <Option key={o.value} value={o.value}>
                    {o.label}
                  </Option>
                ))}
              </Select>

              {periodType !== "" && (
                <DatePicker
                  format="YYYY-MM-DD"
                  placeholder="Reference date (today)"
                  onChange={(d, ds) => setReferenceDate(ds || null)}
                />
              )}

              {periodLabel && <span style={{ color: "#666" }}>Period: {periodLabel}</span>}
            </Space>
          </Col>

          <Col>
            <Button
              className="btn-primary"
              icon={<FileDownloadOutlined />}
              onClick={handleDownloadPDF}
              disabled={!departmentId}
            >
              Download PDF
            </Button>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={data}
          rowKey="expenseManagementId"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>
      <Footer />
    </DashboardLayout>
  );
}

export default EmployeeExpenseRequest;
